'use strict';

// A funding choice is independent of its fiscal editions. Never infer identity
// from EI/CRF: two different funds may have the same funding source.
const json = value => {
  if (value && typeof value === 'object') return value;
  try { return JSON.parse(value || '{}') || {}; } catch (_) { return {}; }
};
const idOf = pot => String(pot?.id ?? pot?.value ?? '');
const fiscalMetadata = pot => json(pot?.metadata).fiscalBudget || {};
const familyKey = pot => fiscalMetadata(pot).familyKey || `legacy:${idOf(pot)}`;
const potYear = pot => String(pot?.fiscal_year || pot?.fiscalYear || pot?.fiscal_year_tag || pot?.fiscalYearTag || '');

function fiscalYearForDate(date) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date || ''));
  if (!match) throw new Error('A scheduled funding date is required for fiscal assignment.');
  const [year, month, day] = match.slice(1).map(Number);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
    throw new Error('Invalid scheduled funding date.');
  }
  const start = year - (month < 4 ? 1 : 0);
  return `${start}-${start + 1}`;
}

function currentFiscalYear(now = new Date()) {
  return fiscalYearForDate(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now));
}

function withoutFiscalYear(value, fiscalYear) {
  const start = /^\d{4}/.exec(String(fiscalYear || ''))?.[0];
  if (!start) return String(value || '');
  const end = String(Number(start) + 1);
  return String(value || '')
    .replace(new RegExp(`\\b${start}\\s*[-–/]\\s*(?:${end}|${end.slice(-2)})\\b`, 'g'), '')
    .replace(new RegExp(`\\b${start}\\b`, 'g'), '')
    .replace(/[-–]\s*[-–]/g, '-').replace(/\s{2,}/g, ' ')
    .replace(/^[\s\-–]+|[\s\-–]+$/g, '').trim();
}

function familyDisplay(pot) {
  const metadata = fiscalMetadata(pot);
  return {
    code: metadata.displayCode || withoutFiscalYear(pot?.code, potYear(pot)),
    name: metadata.displayName || withoutFiscalYear(pot?.name, potYear(pot)),
  };
}

function fundingChoices(pots, selectedId = null) {
  const groups = new Map();
  for (const pot of pots || []) {
    const key = familyKey(pot);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(pot);
  }
  return [...groups.entries()].map(([key, editions]) => {
    const ordered = [...editions].sort((a, b) => Number(idOf(a)) - Number(idOf(b)));
    const selected = ordered.find(pot => idOf(pot) === String(selectedId));
    const available = ordered.filter(pot => !(pot.is_active === 0 || pot.isActive === false));
    const representative = selected || available[0] || ordered[0];
    const display = familyDisplay(representative);
    return Object.assign({}, representative, display, {
      id: idOf(representative), value: idOf(representative),
      familyKey: key,
      fiscalPotIds: ordered.map(idOf),
      fiscalYears: [...new Set(ordered.map(potYear).filter(Boolean))].sort(),
      isActive: available.length > 0,
    });
  });
}

function indexFiscalPots(pots) {
  const byReference = new Map();
  const byFamilyYear = new Map();
  for (const pot of pots || []) {
    byReference.set(idOf(pot), pot);
    // IDs are authoritative. Legacy code references are usable only if unique.
    if (pot.code) byReference.set(pot.code, byReference.has(pot.code) ? null : pot);
    const key = `${familyKey(pot)}|${potYear(pot)}`;
    if (byFamilyYear.has(key)) throw new Error(`Duplicate fiscal edition for funding family ${familyKey(pot)}.`);
    byFamilyYear.set(key, pot);
  }
  return { byReference, byFamilyYear };
}

// The approved report schedule is the input; this layer never invents dates,
// rescales costs to headers, or independently recalculates recurrence.
function fundingPotReference(row = {}) {
  const metadata = json(row.metadata_json);
  return String(metadata.potId || metadata.budgetPotId || row.budget_pot_id || row.plan_budget_pot || '');
}

function allocateFundingDetails(fundingDetails, pots, { overridesEnabled = false, overrides = [] } = {}) {
  if (overridesEnabled || overrides.length) throw new Error('Manual fiscal reassignment is not enabled.');
  const index = indexFiscalPots(pots);
  const allocations = [];
  const issues = [];
  for (const detail of fundingDetails || []) {
    const row = detail.sourceRow || {};
    const metadata = json(row.metadata_json);
    if (metadata.fiscalBudgetAssignment?.override) throw new Error('Manual fiscal reassignment is not enabled.');
    const selected = index.byReference.get(fundingPotReference(row));
    const occurrences = detail.facts?.fundingOccurrences || [];
    for (const [occurrenceIndex, occurrence] of occurrences.entries()) {
      const unassigned = type => {
        issues.push({ type, recordId: detail.facts?.id, occurrenceIndex });
        const amount = Math.round(Number(occurrence.amount) * 100);
        if (!Number.isSafeInteger(amount) || amount < 0) throw new Error('Invalid funding amount.');
        allocations.push({ recordId: detail.facts.id, actionPlanId: row.action_plan_id || null,
          occurrenceIndex, scheduledDate: occurrence.date, fiscalYear: fiscalYearForDate(occurrence.date),
          familyKey: null, sourcePotId: selected ? idOf(selected) : null, potId: null,
          amount: amount / 100, fundingSource: occurrence.fundingSource,
          includedInReports: detail.includedInReports !== false, assignmentMethod: 'unassigned', override: null });
      };
      if (!selected) {
        unassigned('missing_funding_choice');
        continue;
      }
      if (String(selected.pot_type || selected.potType || '').toLowerCase().replace(/[_\s]+/g, ' ') !== 'funding stream') {
        unassigned('ineligible_funding_choice');
        continue;
      }
      const selectedSource = String(selected.funding_source || selected.fundingSource || '').toUpperCase();
      if (selectedSource && occurrence.fundingSource && selectedSource !== String(occurrence.fundingSource).toUpperCase()) {
        unassigned('funding_source_conflict');
        continue;
      }
      const fiscalYear = fiscalYearForDate(occurrence.date);
      const key = familyKey(selected);
      const target = index.byFamilyYear.get(`${key}|${fiscalYear}`);
      const cents = Math.round(Number(occurrence.amount) * 100);
      if (!Number.isSafeInteger(cents) || cents < 0) throw new Error('Invalid funding amount.');
      allocations.push({
        recordId: detail.facts.id, actionPlanId: row.action_plan_id || null,
        occurrenceIndex, scheduledDate: occurrence.date, fiscalYear,
        familyKey: key, sourcePotId: idOf(selected), potId: target ? idOf(target) : null,
        amount: cents / 100, fundingSource: occurrence.fundingSource,
        includedInReports: detail.includedInReports !== false,
        assignmentMethod: 'automatic', override: null,
      });
    }
  }
  return { allocations, issues };
}

function rollupFiscalAllocations(pots, allocations) {
  const byId = new Map((pots || []).map(pot => [idOf(pot), pot]));
  const totals = new Map();
  for (const allocation of allocations || []) {
    let cursor = String(allocation.potId || '');
    const visited = new Set();
    while (cursor && byId.has(cursor)) {
      if (visited.has(cursor)) throw new Error('Budget hierarchy contains a cycle.');
      visited.add(cursor);
      const total = totals.get(cursor) || { reportFundingCents: 0, otherRecordedFundingCents: 0 };
      total[allocation.includedInReports ? 'reportFundingCents' : 'otherRecordedFundingCents'] += Math.round(allocation.amount * 100);
      totals.set(cursor, total);
      cursor = String(byId.get(cursor).parent_id || byId.get(cursor).parentId || '');
    }
  }
  return totals;
}

module.exports = { json, fiscalMetadata, familyKey, familyDisplay, potYear, currentFiscalYear,
  fiscalYearForDate, withoutFiscalYear, fundingChoices, indexFiscalPots,
  allocateFundingDetails, rollupFiscalAllocations, summarizeSavedAllocations, matchesFundingChoice, fundingPotReference };

function summarizeSavedAllocations(interventions) {
  const seen = new Set();
  const totals = new Map();
  for (const intervention of interventions || []) {
    const metadata = json(intervention.metadata || intervention.metadata_json);
    const assignment = metadata.fiscalBudgetAssignment;
    if (assignment?.version !== 1 || !Array.isArray(assignment.allocations)) continue;
    for (const item of assignment.allocations) {
      const key = `${item.recordId}:${item.occurrenceIndex}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (!/^\d{4}-\d{4}$/.test(item.fiscalYear)) continue;
      const amount = Math.round(Number(item.amount) * 100);
      if (!Number.isSafeInteger(amount) || amount < 0) continue;
      totals.set(item.fiscalYear, (totals.get(item.fiscalYear) || 0) + amount);
    }
  }
  return [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([fiscalYear, cents]) => ({ fiscalYear, amount: cents / 100 }));
}

function matchesFundingChoice(option, reference) {
  if (reference === null || reference === undefined || reference === '') return false;
  return [option?.value, option?.id, ...(option?.fiscalPotIds || [])]
    .some(value => value !== null && value !== undefined && String(value) === String(reference));
}
